import { z } from "zod";
import axios from "axios";
import { Log, UserManager, UserManagerSettings, User } from "oidc-client-ts";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { IConfig } from "../types/IConfig";
import { useLocation } from "react-router";

export type AuthContext = {
  userManager: UserManager | null;
  isAuthenticated: boolean;
  getUser: () => Promise<User | null>;
  login: () => Promise<void>;
  renewToken: () => Promise<User>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContext | null>(null);

/**
 * Guarantee `offline_access` is requested, so Entra issues a REFRESH TOKEN.
 *
 * Without it there is no refresh token, and oidc-client-ts can only renew by loading the
 * IdP in a hidden `prompt=none` iframe (silent-renew.html). That iframe is broken by both
 * modern third-party-cookie policies and any `frame-src` CSP, so renewal failed silently
 * (Log.NONE below) and the user was stranded on an expired token — pages stopped loading
 * with an "Authentication Error" until they closed the browser, which is what actually
 * cleared it, because the token lives in sessionStorage.
 *
 * With a refresh token, signinSilent() exchanges it directly against the token endpoint and
 * never opens an iframe at all. Applied defensively here (as well as in the deployed
 * index.html/callback fallbacks) so a scope arriving from config.aspx is also covered.
 *
 * NOTE: the Entra app registration must use the "Single-page application" platform for a
 * refresh token to be returned to browser code — it already must be, since the auth-code
 * exchange in signin-callback.html works today.
 */
export const withOfflineAccess = (scope: string | undefined | null): string => {
  const trimmed = (scope ?? "").trim();
  if (!trimmed) return "openid profile offline_access";
  return /(^|\s)offline_access(\s|$)/.test(trimmed)
    ? trimmed
    : `${trimmed} offline_access`;
};

export const authConfigSchema = z.object({
  stsAuthority: z.string().url(),
  clientId: z.string(),
  clientRoot: z.string().url(),
  clientScope: z.string(),
});
export type AuthConfig = z.infer<typeof authConfigSchema>;

interface AuthProviderProps {
  children: React.ReactNode;
  /* configuration for the oidc service */
  authConfig: AuthConfig | null;
  /**
   * Determines the redirect location after logging out from the OIDC server.
   * - 'jato-homepage': Redirects to jato.com. This is the default behavior and is desired for applications within the shared services.
   * - 'login-page': Redirects to the login page of the application from which the user logged out.
   * @default 'jato-homepage'
   */
  logoutType?: "login-page";
}

export const AuthProvider = ({
  children,
  logoutType = "login-page",
  authConfig = null,
}: AuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();

  const config = window.config as IConfig
    // oidc-client-ts (v3) unconditionally appends "/.well-known/openid-configuration"
    // to `authority` to build the metadata URL. oidc-client (v1) skipped appending when
    // that path was already present. The deployed StsAuthority ends with
    // ".../v2.0/.well-known/openid-configuration", so pass it verbatim and you get a
    // doubled URL (".../openid-configuration/.well-known/openid-configuration") that 404s
    // and surfaces as a CORS error on login. Strip the suffix so v3 appends it exactly once.
    const authority = (config?.stsAuthority ?? '').replace(
      /\/?\.well-known\/openid-configuration\/?$/i,
      ''
    )
    const oldSettings: UserManagerSettings = {
      authority,
      client_id: config?.clientId ?? '',
      redirect_uri: `${config?.clientRoot ?? ''}signin-callback.html`,
      silent_redirect_uri: `${config?.clientRoot ?? ''}silent-renew.html`,
      post_logout_redirect_uri: `${config?.clientRoot ?? ''}`,
      response_type: 'code',
      scope: withOfflineAccess(config?.clientScope),
    }

  // Token storage: oidc-client-ts persists the user/tokens in web storage
  // (sessionStorage by default). That is required here — the authorization-code
  // redirect flow hands the user off from signin-callback.html to the app across a
  // full document navigation, which an in-memory store cannot survive. The residual
  // XSS-token-theft risk is a documented, ACCEPTED Low: it is mitigated by the CSP +
  // security headers (staticwebapp.config.json), short-lived tokens with silent
  // renew, the auth-code flow (no implicit), and — most importantly — the BFF being
  // the real authorization boundary (RLS + server-side role checks), so a stolen,
  // short-lived token is tightly bounded.
  const [userManager] = useState(() => {
    const settings: UserManagerSettings = oldSettings/* {
      authority: authConfig.stsAuthority,
      client_id: authConfig.clientId,
      includeIdTokenInSilentRenew: false,
      post_logout_redirect_uri: authConfig.clientRoot,
      redirect_uri: `${authConfig.clientRoot}signin-callback.html`,
      response_type: "code",
      scope: authConfig.clientScope,
      silent_redirect_uri: `${authConfig.clientRoot}silent-renew.html`,
    } */;

    const userManager = new UserManager(settings);

    Log.setLogger(console);
    Log.setLevel(Log.NONE);

    return userManager;
  });

  const getUser = useCallback(async () => {
    if (userManager) {
      const user = await userManager.getUser();
      setIsAuthenticated(!!user);
      if (user && user.access_token) {
        axios.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${user.access_token}`;
        axios.defaults.headers.common["Accept"] = "application/json";
      }
      return user;
    }
    return null;
  }, [userManager]);

  // `login` is the only member that depended on the current route, and
  // `useLocation()` re-renders this provider on EVERY navigation. Reading it
  // from a ref is what lets the context value below stay referentially stable —
  // see the note there. It also means the state handed to the IdP is the route
  // the user was on when login was *called*, not when the provider last
  // rendered, which is the same value in practice and strictly more correct.
  const locationRef = useRef(location);
  locationRef.current = location;

  const login = useCallback(
    () => userManager.signinRedirect({ state: locationRef.current }),
    [userManager]
  );
  // oidc-client-ts's signinSilent() resolves to User | null (v1 resolved to User).
  // Preserve the Promise<User> contract callers rely on (e.g. ApiService's 401 retry
  // reads renewedUser.access_token) by throwing when a silent renew yields no user.
  const renewToken = useCallback(async (): Promise<User> => {
    const renewed = await userManager.signinSilent();
    if (!renewed) {
      throw new Error("Silent token renewal returned no user");
    }
    return renewed;
  }, [userManager]);
  const logout = useCallback(
    () => userManager.signoutRedirect(),
    [userManager]
  );

  /**
   * PERF: memoized, and it matters well beyond this file.
   *
   * `useAuth()` used to return a brand-new object on every provider render, and
   * this provider re-renders on every navigation because of `useLocation()`
   * above. Around forty `useEffect(..., [auth])` hooks across the app therefore
   * re-fired on every route change — re-fetching options, resource lists,
   * production-order relations and so on for screens that had not changed.
   * `userManager` is created once and the callbacks are all `useCallback`s over
   * it, so the value now only changes when `isAuthenticated` actually flips.
   *
   * Keep every member stable if you add one: a single inline literal or
   * unwrapped closure here silently restores the old behaviour app-wide.
   */
  const value = useMemo<AuthContext>(
    () => ({
      getUser,
      userManager,
      isAuthenticated,
      login,
      renewToken,
      logout,
    }),
    [getUser, userManager, isAuthenticated, login, renewToken, logout]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};


export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};
